export const idlFactory = ({ IDL }) => {
  const InfrastructureReport = IDL.Record({
    'report_id' : IDL.Text,
    'generated_at' : IDL.Nat64,
    'cloud_provider_breakdown' : IDL.Vec(IDL.Tuple(IDL.Text, IDL.Nat32)),
    'recommendations_summary' : IDL.Vec(IDL.Tuple(IDL.Text, IDL.Nat32)),
    'total_devices' : IDL.Nat32,
    'findings_summary' : IDL.Vec(IDL.Tuple(IDL.Text, IDL.Nat32)),
    'total_applications' : IDL.Nat32,
    'health_score' : IDL.Float32,
    'os_distribution' : IDL.Vec(IDL.Tuple(IDL.Text, IDL.Nat32)),
    'device_breakdown' : IDL.Vec(IDL.Tuple(IDL.Text, IDL.Nat32)),
  });
  const FindingType = IDL.Variant({
    'ConfigurationDrift' : IDL.Null,
    'CostOptimization' : IDL.Null,
    'PerformanceIssue' : IDL.Null,
    'SecurityViolation' : IDL.Null,
    'Anomaly' : IDL.Null,
    'ComplianceViolation' : IDL.Null,
  });
  const Severity = IDL.Variant({
    'Low' : IDL.Null,
    'High' : IDL.Null,
    'Info' : IDL.Null,
    'Medium' : IDL.Null,
    'Critical' : IDL.Null,
  });
  const Finding = IDL.Record({
    'affected_resources' : IDL.Vec(IDL.Text),
    'title' : IDL.Text,
    'metadata' : IDL.Vec(IDL.Tuple(IDL.Text, IDL.Text)),
    'description' : IDL.Text,
    'created_at' : IDL.Nat64,
    'finding_id' : IDL.Text,
    'finding_type' : FindingType,
    'severity' : Severity,
  });
  const AnomalyReport = IDL.Record({
    'reasons' : IDL.Vec(IDL.Text),
    'device_name' : IDL.Text,
    'device_id' : IDL.Text,
    'average_similarity' : IDL.Float32,
    'anomaly_score' : IDL.Float32,
    'peer_count' : IDL.Nat32,
  });
  const DeviceMetadata = IDL.Record({
    'ip' : IDL.Text,
    'region' : IDL.Text,
    'model' : IDL.Text,
    'public_ip' : IDL.Text,
    'hostname' : IDL.Text,
    'device_type' : IDL.Text,
    'device_id' : IDL.Text,
    'os_family' : IDL.Text,
  });
  const ServiceDevice = IDL.Record({
    'config_files' : IDL.Vec(IDL.Text),
    'role' : IDL.Text,
    'hostname' : IDL.Text,
    'device_id' : IDL.Text,
    'ip_address' : IDL.Text,
  });
  const ServiceTier = IDL.Record({
    'tier_name' : IDL.Text,
    'tier_level' : IDL.Nat32,
    'devices' : IDL.Vec(ServiceDevice),
  });
  const ServiceConnection = IDL.Record({
    'to_device' : IDL.Text,
    'port' : IDL.Opt(IDL.Nat32),
    'connection_type' : IDL.Text,
    'from_device' : IDL.Text,
  });
  const ServiceMap = IDL.Record({
    'tiers' : IDL.Vec(ServiceTier),
    'discovery_logs' : IDL.Vec(IDL.Text),
    'service_name' : IDL.Text,
    'total_devices' : IDL.Nat32,
    'connections' : IDL.Vec(ServiceConnection),
    'discovered_at' : IDL.Nat64,
  });
  const GraphNode = IDL.Record({
    'id' : IDL.Text,
    'ip' : IDL.Text,
    'config_files' : IDL.Vec(IDL.Text),
    'tier_level' : IDL.Nat32,
    'tier' : IDL.Text,
    'hostname' : IDL.Text,
  });
  const GraphEdge = IDL.Record({
    'to' : IDL.Text,
    'from' : IDL.Text,
    'port' : IDL.Opt(IDL.Nat32),
    'connection_type' : IDL.Text,
  });
  const GraphUpdate = IDL.Record({
    'node_data' : IDL.Opt(GraphNode),
    'action' : IDL.Text,
    'edge_data' : IDL.Opt(GraphEdge),
  });
  const ServiceMapGenerationStep = IDL.Record({
    'graph_update' : IDL.Opt(GraphUpdate),
    'log_message' : IDL.Text,
    'timestamp' : IDL.Text,
    'log_level' : IDL.Text,
  });
  const ServiceMapGenerationResponse = IDL.Record({
    'generation_steps' : IDL.Vec(ServiceMapGenerationStep),
    'service_name' : IDL.Text,
    'final_service_map' : ServiceMap,
  });
  const Priority = IDL.Variant({
    'Low' : IDL.Null,
    'High' : IDL.Null,
    'Medium' : IDL.Null,
    'Critical' : IDL.Null,
  });
  const RecommendationType = IDL.Variant({
    'Cost' : IDL.Null,
    'Security' : IDL.Null,
    'Compliance' : IDL.Null,
    'Reliability' : IDL.Null,
    'Performance' : IDL.Null,
    'Maintenance' : IDL.Null,
  });
  const Recommendation = IDL.Record({
    'title' : IDL.Text,
    'recommendation_id' : IDL.Text,
    'metadata' : IDL.Vec(IDL.Tuple(IDL.Text, IDL.Text)),
    'estimated_impact' : IDL.Text,
    'description' : IDL.Text,
    'created_at' : IDL.Nat64,
    'priority' : Priority,
    'related_findings' : IDL.Vec(IDL.Text),
    'action_items' : IDL.Vec(IDL.Text),
    'recommendation_type' : RecommendationType,
  });
  const SessionStatus = IDL.Variant({
    'Failed' : IDL.Null,
    'InProgress' : IDL.Null,
    'Completed' : IDL.Null,
  });
  const AnalysisSession = IDL.Record({
    'status' : SessionStatus,
    'user_query' : IDL.Text,
    'session_id' : IDL.Text,
    'recommendations' : IDL.Vec(Recommendation),
    'created_at' : IDL.Nat64,
    'findings' : IDL.Vec(Finding),
    'completed_at' : IDL.Opt(IDL.Nat64),
  });
  return IDL.Service({
    'analyze_infrastructure' : IDL.Func(
        [],
        [IDL.Variant({ 'Ok' : InfrastructureReport, 'Err' : IDL.Text })],
        [],
      ),
    'audit_security_compliance' : IDL.Func(
        [],
        [IDL.Variant({ 'Ok' : IDL.Vec(Finding), 'Err' : IDL.Text })],
        [],
      ),
    'call_ada' : IDL.Func(
        [IDL.Text],
        [IDL.Variant({ 'Ok' : IDL.Text, 'Err' : IDL.Text })],
        [],
      ),
    'complete_session' : IDL.Func(
        [IDL.Text],
        [IDL.Variant({ 'Ok' : IDL.Null, 'Err' : IDL.Text })],
        [],
      ),
    'create_analysis_session' : IDL.Func(
        [IDL.Text],
        [IDL.Variant({ 'Ok' : IDL.Text, 'Err' : IDL.Text })],
        [],
      ),
    'detect_anomalies' : IDL.Func(
        [IDL.Float32],
        [IDL.Variant({ 'Ok' : IDL.Vec(AnomalyReport), 'Err' : IDL.Text })],
        [],
      ),
    'fetch_all_device_metadata' : IDL.Func(
        [IDL.Bool],
        [IDL.Variant({ 'Ok' : IDL.Vec(DeviceMetadata), 'Err' : IDL.Text })],
        [],
      ),
    'generate_service_map' : IDL.Func(
        [IDL.Text],
        [IDL.Variant({ 'Ok' : ServiceMap, 'Err' : IDL.Text })],
        [],
      ),
    'generate_service_map_live' : IDL.Func(
        [IDL.Text],
        [
          IDL.Variant({
            'Ok' : ServiceMapGenerationResponse,
            'Err' : IDL.Text,
          }),
        ],
        [],
      ),
    'get_ada_principal' : IDL.Func([], [IDL.Opt(IDL.Principal)], ['query']),
    'get_all_configs' : IDL.Func(
        [],
        [
          IDL.Variant({
            'Ok' : IDL.Vec(IDL.Tuple(IDL.Text, IDL.Text, IDL.Text, IDL.Text)),
            'Err' : IDL.Text,
          }),
        ],
        [],
      ),
    'get_all_findings' : IDL.Func([], [IDL.Vec(Finding)], ['query']),
    'get_all_recommendations' : IDL.Func(
        [],
        [IDL.Vec(Recommendation)],
        ['query'],
      ),
    'get_all_sessions' : IDL.Func([], [IDL.Vec(AnalysisSession)], ['query']),
    'get_configs_for_device' : IDL.Func(
        [IDL.Text],
        [
          IDL.Variant({
            'Ok' : IDL.Vec(
              IDL.Tuple(
                IDL.Text,
                IDL.Text,
                IDL.Text,
                IDL.Vec(IDL.Tuple(IDL.Text, IDL.Text)),
              )
            ),
            'Err' : IDL.Text,
          }),
        ],
        [],
      ),
    'get_configs_for_service' : IDL.Func(
        [IDL.Text],
        [
          IDL.Variant({
            'Ok' : IDL.Vec(IDL.Tuple(IDL.Text, IDL.Text, IDL.Text, IDL.Text)),
            'Err' : IDL.Text,
          }),
        ],
        [],
      ),
    'get_finding' : IDL.Func([IDL.Text], [IDL.Opt(Finding)], ['query']),
    'get_findings_by_severity' : IDL.Func(
        [Severity],
        [IDL.Vec(Finding)],
        ['query'],
      ),
    'get_recommendation' : IDL.Func(
        [IDL.Text],
        [IDL.Opt(Recommendation)],
        ['query'],
      ),
    'get_recommendations_by_type' : IDL.Func(
        [RecommendationType],
        [IDL.Vec(Recommendation)],
        ['query'],
      ),
    'get_session' : IDL.Func([IDL.Text], [IDL.Opt(AnalysisSession)], ['query']),
    'get_stats' : IDL.Func(
        [],
        [IDL.Vec(IDL.Tuple(IDL.Text, IDL.Nat32))],
        ['query'],
      ),
    'get_vector_db_principal' : IDL.Func(
        [],
        [IDL.Opt(IDL.Principal)],
        ['query'],
      ),
    'recommend_cost_optimizations' : IDL.Func(
        [],
        [IDL.Variant({ 'Ok' : IDL.Vec(Recommendation), 'Err' : IDL.Text })],
        [],
      ),
    'set_ada_principal' : IDL.Func(
        [IDL.Principal],
        [IDL.Variant({ 'Ok' : IDL.Null, 'Err' : IDL.Text })],
        [],
      ),
    'set_vector_db_principal' : IDL.Func(
        [IDL.Principal],
        [IDL.Variant({ 'Ok' : IDL.Null, 'Err' : IDL.Text })],
        [],
      ),
  });
};
export const init = ({ IDL }) => { return []; };
